<script setup>
import { computed } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PartnersLayout from '@/Layouts/PartnersLayout.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'

defineOptions({ layout: PartnersLayout })

const { t } = useI18n()
const page = usePage()
const base = computed(() => page.props.base || '')
const props = defineProps({
  type: { type: String, default: 'supplier' },
  form: { type: Object, default: () => ({}) },
  errors: { type: Array, default: () => [] }
})

const form = useForm({
  name: props.form.name || '',
  first_name: props.form.first_name || '',
  last_name: props.form.last_name || '',
  phone: props.form.phone || '',
  email: props.form.email || '',
  password: '',
  password_confirmation: '',
  contact_channels_attributes: props.form.contact_channels_attributes || [],
  website: '' // honeypot
})

function submit() {
  form.transform((d) => ({
    type: props.type,
    partner: {
      ...d,
      contact_channels_attributes: d.contact_channels_attributes
        .filter((c) => c.handle !== '' || c._destroy)
        .map((c, i) => ({ ...c, position: i }))
    }
  })).post(`${base.value}/register`, { preserveScroll: true })
}
</script>

<template>
  <Head>
    <title>{{ t(`partners.types.${type}.name`) }} — {{ t('partners.register.head_title') }}</title>
  </Head>
  <h1 class="r-title">
    {{ t('partners.register.title', { type: t(`partners.types.${type}.name`) }) }}
  </h1>
  <p class="r-lede">
    {{ t(`partners.types.${type}.value`) }}
  </p>

  <ul
    v-if="errors.length"
    class="r-errors"
  >
    <li
      v-for="(e, i) in errors"
      :key="i"
    >
      {{ e }}
    </li>
  </ul>

  <form
    class="r-form"
    @submit.prevent="submit"
  >
    <label class="r-field">
      <span class="r-label">{{ t('partners.register.fields.name') }}</span>
      <input
        v-model="form.name"
        type="text"
        required
        class="r-input"
      >
    </label>
    <div class="r-row">
      <label class="r-field">
        <span class="r-label">{{ t('partners.register.fields.first_name') }}</span>
        <input
          v-model="form.first_name"
          type="text"
          class="r-input"
        >
      </label>
      <label class="r-field">
        <span class="r-label">{{ t('partners.register.fields.last_name') }}</span>
        <input
          v-model="form.last_name"
          type="text"
          class="r-input"
        >
      </label>
    </div>
    <label class="r-field">
      <span class="r-label">{{ t('partners.register.fields.email') }}</span>
      <input
        v-model="form.email"
        type="email"
        autocomplete="email"
        required
        class="r-input"
      >
    </label>
    <label class="r-field">
      <span class="r-label">{{ t('partners.register.fields.phone') }}</span>
      <PhoneInput v-model="form.phone" />
    </label>

    <div class="r-field">
      <span class="r-label">{{ t('partners.register.fields.channels') }}</span>
      <CustomerChannels v-model="form.contact_channels_attributes" />
    </div>

    <label class="r-field">
      <span class="r-label">{{ t('partners.register.fields.password') }}</span>
      <input
        v-model="form.password"
        type="password"
        autocomplete="new-password"
        required
        class="r-input"
      >
    </label>
    <label class="r-field">
      <span class="r-label">{{ t('partners.register.fields.password_confirmation') }}</span>
      <input
        v-model="form.password_confirmation"
        type="password"
        autocomplete="new-password"
        required
        class="r-input"
      >
    </label>

    <!-- Honeypot: off-screen; bots fill it, humans don't. -->
    <input
      v-model="form.website"
      type="text"
      tabindex="-1"
      autocomplete="off"
      class="r-hp"
      aria-hidden="true"
    >

    <button
      type="submit"
      :disabled="form.processing"
      class="r-submit"
    >
      {{ form.processing ? t('partners.register.submit.loading') : t('partners.register.submit.idle') }}
    </button>
  </form>
</template>

<style scoped>
.r-title { font-size: 1.25rem; font-weight: 700; color: var(--ink); margin: 0 0 0.35rem; }
.r-lede { color: var(--inkSoft); font-size: 0.9375rem; margin: 0 0 1rem; line-height: 1.5; }
.r-errors { margin: 0 0 1rem; padding: 0.75rem 1rem 0.75rem 2rem; border-radius: 0.5rem; background: color-mix(in oklch, var(--danger) 10%, var(--surface)); color: var(--danger); font-size: 0.875rem; }
.r-form { display: grid; gap: 0.75rem; }
.r-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.r-field { display: grid; gap: 0.3rem; }
.r-label { font-size: 0.8125rem; color: var(--inkSoft); }
.r-input { width: 100%; padding: 0.55rem 0.75rem; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-input, 8px); color: var(--ink); font-size: 0.9375rem; }
.r-input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.r-hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.r-submit { margin-top: 0.25rem; padding: 0.65rem 1rem; border: 0; border-radius: var(--radius-button, 8px); background: var(--accent); color: var(--accentInk); font-weight: 600; cursor: pointer; }
.r-submit:disabled { opacity: 0.6; cursor: progress; }
</style>
